/**
 * Payer Connections — shared types.
 *
 * The fold, the roster helpers, and the presentational components are pure.
 * GDN-3406 wires the clinic section against the real endpoint (see
 * `store/`); GDN-3408 will wire the roster.
 *
 * `EnrollmentRow` is no longer hand-written: it is the generated
 * `LiveEnrollmentResponse` from `GET /v2/provider/enrollments/live`
 * (GDN-3399), re-exported so the fold and the endpoint cannot drift. Per the
 * plot AGENTS.md rule, generated request/response types are re-exported
 * through the feature's type wrapper rather than duplicated here.
 */

import type { LiveEnrollmentResponse } from '@store/generated/enrollments';

/**
 * The two transaction rails this release folds. Typed as `string` (rather
 * than a `'837P' | '835'` literal union) because Stedi may return a rail we
 * haven't seen — narrowing here would just be a lint-flagged no-op.
 */
export type TransactionType = string;

/** The two rails this release names explicitly; anything else is still a valid `TransactionType`. */
export const KNOWN_TRANSACTION_TYPES = ['837P', '835'] as const;

/**
 * Raw Stedi enrollment status values, per PRD section 4 / AC-3. Typed as
 * `string` so a status Stedi adds that we haven't seen still type-checks —
 * `foldStatus` in fold.ts has an explicit `default` branch for it.
 */
export type EnrollmentStatus = string;

/** The statuses named explicitly by AC-3/AC-6; `foldStatus`/`resolveWhatsHappening` fall back for anything else. */
export const KNOWN_ENROLLMENT_STATUSES = [
  'DRAFT',
  'CANCELED',
  'PROVIDER_ACTION_REQUIRED',
  'REJECTED',
  'STEDI_ACTION_REQUIRED',
  'PROVISIONING',
  'LIVE',
] as const;

/**
 * One enrollment row as returned by the read-only Stedi passthrough
 * (GDN-3399), org-wide, one row per payer per transaction rail.
 *
 * This is the generated response row verbatim. Two of its fields are
 * nullable where the hand-written predecessor required them —
 * `payer_name` (the backend resolves it through the payers catalog and
 * falls back to the raw Stedi payer id, so it is populated in practice)
 * and `stedi_payer_id`. `fold.ts` carries the fallbacks; nothing widens or
 * narrows the generated type.
 */
export type EnrollmentRow = LiveEnrollmentResponse;

/** The live passthrough's whole response: when it answered, whether the org has a Stedi key, and the rows. */
export type { LiveEnrollmentsResponse } from '@store/generated/enrollments';
export type {
  LiveEnrollmentStatusResponse,
  LiveEnrollmentStatusRow,
  PayerEvidenceRequest,
  PayerEvidenceResponse,
  PayerLoadOrganizationResponse,
  PayerLoadOrganizationsResponse,
  PayerReceiptEvidenceResponse,
} from '@store/generated/enrollments';

/** Copy perspective only; does not change data scope or permissions. */
export type PayerAudience = 'clinic' | 'staff';

/**
 * The folded states a payer can be in, per PRD section 4 / AC-3, AC-4.
 * Severity order (most to least severe): needs_us > needs_customer > waiting > ready > canceled.
 * `canceled` (GDN-3684) is terminal: nothing is owed by anyone, so it never
 * outranks a rail that still needs work and blocks no party.
 * `live` is not a severity rung — a payer only reaches it once `ready` AND the
 * same payer has a verified production 835 receipt (AC-4); live payers are excluded from
 * the row list.
 */
export type PayerState = 'needs_us' | 'needs_customer' | 'waiting' | 'ready' | 'canceled' | 'live';

/** Non-live fold states, in severity order (most severe first). Used for sorting and summary chips. */
export const SEVERITY_ORDER: readonly PayerState[] = [
  'needs_us',
  'needs_customer',
  'waiting',
  'ready',
  'canceled',
] as const;

/** The party blocking a payer/org, per AC-14. */
export type BlockedParty = 'JOTPSYCH' | 'CUSTOMER' | 'PAYER' | 'NONE';

/** Party rank for roster sort, per AC-17: JOTPSYCH, CUSTOMER, PAYER, NONE. */
export const PARTY_ORDER: readonly BlockedParty[] = [
  'JOTPSYCH',
  'CUSTOMER',
  'PAYER',
  'NONE',
] as const;

/** One rail's contribution to a payer's fold — kept for "what's happening" and Live determination. */
export interface RailView {
  enrollmentId?: EnrollmentRow['stedi_enrollment_id'];
  transactionType: TransactionType;
  status: EnrollmentStatus;
  state: Exclude<PayerState, 'live'>;
  statusMessage?: string | null;
  rejectionReason?: string | null;
  submittedAt?: string | null;
  createdAt?: string | null;
  /** Stedi's `statusLastUpdatedAt` — the anchor for status age, never for days blocked. */
  statusUpdatedAt?: string | null;
  stediProviderId?: string | null;
  providerNpi?: string | null;
  portalUrl?: string | null;
}

/**
 * One selected enrollment's exact Stedi status for staff diagnostics. This is
 * a view projection of the generated enrollment row: values are never
 * classified or rewritten. One entry per enrollment identity (provider,
 * payer, rail) — superseded attempts are dropped by `selectLatestEnrollments`.
 */
export interface StediStatusView {
  enrollmentId: EnrollmentRow['stedi_enrollment_id'];
  payerName: string;
  transactionType: TransactionType;
  status: EnrollmentStatus;
  statusUpdatedAt?: string | null;
  stediProviderId?: string | null;
  providerNpi?: string | null;
}

/** One payer folded to a single state, per AC-3–AC-6. */
export interface FoldedPayer {
  /** Enrollment selected for the displayed status, link and analytics before rails are keyed by type. */
  selectedRail?: RailView;
  payerId: string;
  payerName: string;
  state: PayerState;
  /**
   * Selected rails for this payer, keyed by transaction type. When several
   * providers hold the same rail, each is keyed `<type>:<stedi_provider_id>`
   * instead so none is lost.
   */
  rails: Partial<Record<string, RailView>>;
  /** Selected enrollments' raw statuses for staff display, one per identity/rail. */
  stediStatuses: StediStatusView[];
  /** AC-6 text: verbatim status_message/rejection_reason, else canned copy. */
  whatsHappening: string;
  /** Present when a Needs-you row should link out to Stedi (AC-7 wires the link; we only carry the URL). */
  portalUrl?: string | null;
  /** Production receipt evidence, separately from enrollment status. */
  hasReceivedClaim: EnrollmentRow['has_received_claim'];
}

/** Org-wide fold result. */
export interface FoldResult {
  /** Non-live payers, sorted by severity then payer name (AC-8). */
  payers: FoldedPayer[];
  /** Count of payers in each state, including live. */
  counts: Record<PayerState, number>;
  /** Ready enrollment with a confirmed negative receipt check; excludes unknowns. */
  readyCount: number;
  /** Total payers enrolling (i.e. not live). */
  enrollingCount: number;
  /** Total live payers. */
  liveCount: number;
  /** Payers whose receipt evidence is unavailable or inconsistent. */
  unknownCount: number;
}

/** Roster row input: one organization's enrollment rows with per-payer receipt evidence (AC-4). */
export interface OrgEnrollmentInput {
  organizationId: string;
  organizationName: string;
  rows: EnrollmentRow[];
}

/** One organization folded for the staff roster (AC-14–AC-17). */
export interface RosterRow {
  organizationId: string;
  organizationName: string;
  blockedParty: BlockedParty;
  /** Whole days since the oldest submitted_at/created_at across the most severe payer's rails, or null (AC-15). */
  daysBlocked: number | null;
  /** Non-live payers for the chip strip, in fold order (severity, then name). */
  payers: FoldedPayer[];
  liveCount: number;
  /** Payers whose receipt evidence is unavailable or inconsistent. */
  unknownCount: number;
}
